import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components'
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons'
import { FamilyLawModuleDto, getDefaultOffspring, OffspringDto } from '../../../../../hooks/module/useFamilyLaw'
import { Body } from './Body'
import { useFormikContext } from 'formik'
import useViewport, { Viewport } from '../../../../../hooks/useViewport'
import { RelationshipType } from '../../Relationship'
import * as Yup from 'yup'
import { ValidationError } from 'yup'
import Form from '../../../Form'
import { FamilyLawDialogAction, FamilyLawDialogProps } from '../../../../../pages/File/Registration/FamilyLawOverview'

const useStyles = makeStyles({
    formRow: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    side: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    fullName: {
        gridTemplateColumns: '2fr 1fr 4fr',
    },
    firstNames: {
        gridTemplateColumns: '1fr 5fr',
    },
    details: {
        gridTemplateColumns: '1fr 2fr',
    },
    minWidth: {
        minWidth: 0,
    },
})

export const MainstayOptions: any[] = [
    { key: 'CLIENT', text: 'Bij cliënt' },
    { key: 'COUNTER', text: 'Bij wederpartij' },
    { key: 'JOINT', text: 'Bij beide ouders' },
    { key: 'SEPARATE', text: 'Zelfstandig' },
    { key: 'MISC', text: 'Overig' },
]

export const CustodyOptions: any[] = [
    { key: 'JOINT', text: 'Gezamenlijk' },
    { key: 'CLIENT', text: 'Cliënt' },
    { key: 'COUNTER', text: 'Wederpartij' },
    { key: 'MISC', text: 'Overig' },
]

export enum Acknowledgement {
    Birth = 'BIRTH',
    Later = 'LATER',
    No = 'NO',
    ByLaw = 'BY_LAW',
    Misc = 'MISC',
}

const AcknowledgementObjects: any[] = [
    { key: Acknowledgement.Birth, text: 'Ja, bij geboorte' },
    { key: Acknowledgement.Later, text: 'Ja, later' },
    { key: Acknowledgement.No, text: 'Niet erkend' },
    { key: Acknowledgement.ByLaw, text: 'Van rechtswege' },
    { key: Acknowledgement.Misc, text: 'Overig' },
]

export const MainstayMap: Map<string, string> = new Map(MainstayOptions.map(option => [option.key, option.text]))
export const CustodyMap: Map<string, string> = new Map(CustodyOptions.map(option => [option.key, option.text]))
export const AcknowledgementMap: Map<string, string> = new Map(AcknowledgementObjects.map(option => [option.key, option.text]))

const validationSchema = Yup.object().shape({
    child: Yup.object().shape({
        name: Yup.object().shape({
            given: Yup.string().required('Roepnaam is verplicht'),
            last: Yup.string().required('Achternaam is verplicht'),
            initials: Yup.string().required('Voorletters zijn verplicht'),
            first: Yup.string().required('Voornamen zijn verplicht'),
        }),
        gender: Yup.string().required('Geslacht is verplicht'),
        dateOfBirth: Yup.string()
            .required('Geboortedatum is verplicht')
            .test('valid-date', 'Geboortedatum is later dan vandaag', value => (!!value.trim() ? new Date() > new Date(value) : true)),
        identificationNumber: Yup.string().required('BSN is verplicht'),
    }),
})

export default function OffspringDialog(props: FamilyLawDialogProps): ReactElement {
    const { values } = useFormikContext<FamilyLawModuleDto>()
    const { isViewport, viewport } = useViewport()
    const styles = useStyles()
    const horizontal = useMemo(() => isViewport('>=', Viewport.XL), [viewport])
    const baseOffspring = useMemo(() => {
        if (props.action === FamilyLawDialogAction.Add) {
            return values.relationshipType === RelationshipType.Cohabitation
                ? getDefaultOffspring(Acknowledgement.Birth)
                : getDefaultOffspring(Acknowledgement.ByLaw)
        } else {
            return values.offspring[props.index]
        }
    }, [props.open])
    const [offspring, setOffspring] = useState<OffspringDto>(baseOffspring)
    const [validationError, setValidationError] = useState([])

    return (
        <Dialog
            open={props.open}
            onOpenChange={(_, data) => {
                props.setOpen(data.open)
            }}
        >
            <DialogSurface>
                <Form globalErrors={validationError}>
                    <DialogBody>
                        <DialogTitle>Kind toevoegen</DialogTitle>
                        <DialogContent>
                            <Body offspring={offspring} setOffspring={setOffspring} {...props} styles={styles} horizontal={horizontal} />
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="transparent" icon={<DismissRegular />}>
                                    Annuleren
                                </Button>
                            </DialogTrigger>
                            <Button
                                appearance="primary"
                                icon={<CheckmarkRegular />}
                                onClick={() => {
                                    validationSchema.validate(offspring, { abortEarly: false }).then(
                                        () => {
                                            props.action === FamilyLawDialogAction.Add ? props.push(offspring) : props.replace(props.index, offspring)
                                            props.setOpen(false)
                                        },
                                        (error: ValidationError) => {
                                            setValidationError(error.inner)
                                        },
                                    )
                                }}
                            >
                                Opslaan
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </Form>
            </DialogSurface>
        </Dialog>
    )
}
